import React, { ReactNode, Suspense, useEffect, useState, useRef } from 'react'
import cx from 'classnames'
import { SidebarType } from 'configs/constants/enums'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { Box, Container } from '@chakra-ui/react'
import styles from './verticalLayout.module.scss'
import GlobalSpinner from 'components/GlobalSpinner'
import LeftSidebar from 'components/LeftSidebar'
import Topbar from 'components/Topbar'
import PlayTopbar from 'components/Topbar/PlayTopbar'
import BottomNav from 'pages/PlayDashboardPage/components/bottom-nav'
import BetterOnDesktopBanner from 'components/BetterOnDesktopBanner'
import NotificationPermissionBanner from 'components/NotificationPermissionBanner'
import BillingStatusBanner from 'components/BillingStatusBanner'
import { Outlet } from 'react-router-dom'
import WorkEmailVerificationFlow from 'components/EmailVerificationFlow/WorkEmailVerificationFlow'
import { MOBILE_MAX_WIDTH } from 'constants/mobile-page-template'

interface IVerticalLayoutProps {
  children?: ReactNode
  // When true, mounts PlayTopbar (dark-mode, 48px, Figma spec) instead of the
  // default Topbar. Used for /play-dashboard and /play-profile.
  usePlayTopbar?: boolean
  // Play-route profile-type guard for LeftSidebar items. Threaded straight
  // through to LeftSidebar -> SideBarContent. Undefined on org routes.
  activeProfileType?: import('constants/play-profile').PlayProfileRole
}

const VerticalLayout = (props: IVerticalLayoutProps) => {
  const { children, usePlayTopbar, activeProfileType } = props
  const { generalStore, spinnerStore } = useStores()
  const { showSidebarMobile } = generalStore
  const { isLoading } = spinnerStore
  // Sidebar starts expanded by default on every breakpoint above mobile; the
  // user opts into the condensed width via the topbar toggle.
  const [sidebarType, setSidebarType] = useState<SidebarType | ''>(SidebarType.FIXED)
  // Ref mirror of sidebarType so the resize listener (captured once per effect)
  // can read the *current* value instead of a stale closure snapshot.
  const sidebarTypeRef = useRef<SidebarType | ''>(SidebarType.FIXED)
  const ref = useRef<HTMLDivElement>(null)
  const isCondensed = sidebarType === SidebarType.CONDENSED

  function updateDimensions() {
    // Play routes default to EXPANDED (FIXED) on every breakpoint above mobile —
    // user controls collapse via the PlayTopbar hamburger button.
    if (usePlayTopbar) {
      if (window.innerWidth <= MOBILE_MAX_WIDTH) {
        changeSidebarTypeUI('')
      } else if (sidebarTypeRef.current === '') {
        // Coming back from mobile to md+, restore the default expanded state.
        changeSidebarTypeUI(SidebarType.FIXED)
      }
      return
    }
    //* activate the condensed sidebar if smaller devices like ipad or tablet
    if (window.innerWidth >= 768 && window.innerWidth <= 1028) {
      changeSidebarTypeUI(SidebarType.CONDENSED)
    } else if (window.innerWidth > 1028) {
      changeSidebarTypeUI(SidebarType.FIXED)
    } else if (window.innerWidth <= MOBILE_MAX_WIDTH) {
      changeSidebarTypeUI('')
    } else if (sidebarTypeRef.current === '') {
      changeSidebarTypeUI(SidebarType.FIXED)
    }
  }

  function changeSidebarTypeUI(newType: SidebarType | ''): void {
    setSidebarType(newType)
    sidebarTypeRef.current = newType
    document.body.setAttribute('data-leftbar-compact-mode', newType)
  }

  function toggleSidebarCollapse() {
    changeSidebarTypeUI(sidebarType === SidebarType.CONDENSED ? SidebarType.FIXED : SidebarType.CONDENSED)
  }

  useEffect(() => {
    if (document.body) document.body.setAttribute('data-leftbar-theme', 'dark')

    // Set initial sidebar state BEFORE registering the resize listener, so any
    // resize event that fires during the same tick reads the correct state.
    if (usePlayTopbar) {
      // Play routes: default EXPANDED (FIXED) on md+, hidden on mobile.
      changeSidebarTypeUI(window.innerWidth <= MOBILE_MAX_WIDTH ? '' : SidebarType.FIXED)
    } else if (window.innerWidth >= 768 && window.innerWidth <= 1028) {
      //* activate the condensed sidebar if smaller devices like ipad or tablet
      changeSidebarTypeUI(SidebarType.CONDENSED)
    } else if (window.innerWidth <= MOBILE_MAX_WIDTH) {
      changeSidebarTypeUI('')
    }

    window.addEventListener('resize', updateDimensions)

    return () => {
      window.removeEventListener('resize', updateDimensions)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usePlayTopbar])

  return (
    <div className="app">
      {isLoading && <GlobalSpinner />}
      {/* Pinned above every other piece of chrome, and the offset the rest of it
          steps down by. Self-gating: mobile, coach+, not dismissed this session. */}
      <BetterOnDesktopBanner />
      <div className={cx(styles.wrapper, 'wrapper')}>
        <div ref={ref} className={styles.sidebarContainer}>
          <Suspense fallback={<GlobalSpinner />}>
            <LeftSidebar
              isCondensed={isCondensed}
              showSidebarMobile={showSidebarMobile}
              setShowSidebarMobile={showSidebarMobile => generalStore.setShowSidebarMobile(showSidebarMobile)}
              activeProfileType={activeProfileType}
            />
          </Suspense>
        </div>

        <div className={cx('content-page', styles.contentPageWithTransition)}>
          <div className="content">
            <Suspense fallback={<GlobalSpinner />}>
              {usePlayTopbar ? (
                <>
                  {/* Unified dark chrome — PlayTopbar is responsive (search hides
                      on base, right icon group renders on all breakpoints) so a
                      single component drives both desktop and mobile. No separate
                      mobile header — keep one source of truth for the topbar. */}
                  <PlayTopbar
                    toggleSidebarCollapse={toggleSidebarCollapse}
                    setShowSidebarMobile={() => generalStore.setShowSidebarMobile(!showSidebarMobile)}
                    showSidebarMobile={showSidebarMobile}
                    isCondensed={isCondensed}
                  />
                  {/* Route-aware HeaderBar (breadcrumb / page title) renders below
                      the dark chrome. The legacy nav chrome is hidden so we don't
                      double up with the new header. */}
                  <Topbar
                    hideLogo
                    hideChrome
                    toggleSidebarCollapse={toggleSidebarCollapse}
                    setShowSidebarMobile={() => generalStore.setShowSidebarMobile(!showSidebarMobile)}
                    showSidebarMobile={showSidebarMobile}
                  />
                </>
              ) : (
                <Topbar
                  hideLogo={true}
                  toggleSidebarCollapse={toggleSidebarCollapse}
                  setShowSidebarMobile={() => generalStore.setShowSidebarMobile(!showSidebarMobile)}
                  showSidebarMobile={showSidebarMobile}
                />
              )}
            </Suspense>

            <Container maxW="full" px={0}>
              <BillingStatusBanner />
              <NotificationPermissionBanner />
              <Suspense fallback={<GlobalSpinner />}>
                <Outlet />
                <WorkEmailVerificationFlow />
              </Suspense>
            </Container>
          </div>
        </div>
      </div>
      {/* Mobile-only bottom nav. Mounts globally for every authed route since
          PlayTopbar is hidden on mobile — BottomNav is the mobile navigation
          surface (home / schedule / assignments / teams / more overflow). */}
      {usePlayTopbar && (
        <Box display={{ base: 'block', md: 'none' }}>
          {/* Same profile context the sidebar gets, so the two resolve the same
              pages for the same viewer. */}
          <BottomNav activeProfileType={activeProfileType} />
        </Box>
      )}
    </div>
  )
}

export default observer(VerticalLayout)
